import type { Asset, Item } from './item';
import type { MonthIndex } from './time';

interface Dated {
  month: MonthIndex;
  anchors?: { month?: unknown } | undefined;
}

/** Untied entries move by `delta`; tied ones stay with their key date. Sorted by month. */
function shiftUntied<T extends Dated>(entries: readonly T[], delta: number): T[] {
  return entries
    .map((e) =>
      e.anchors?.month === undefined ? { ...e, month: (e.month + delta) as MonthIndex } : e,
    )
    .sort((a, b) => a.month - b.month);
}

/**
 * Moves an item's start, with the dates that belong to it: instalments and early repayments keep
 * their distance from the start, and an untied end before the new start moves up to it. Tied
 * dates and the sale stay where they are; `startRange` and `keyDateRanges` keep the start clear of
 * them. Used by the editor and when a key date moves the start.
 */
export function moveStart<T extends Item>(item: T, start: MonthIndex): T {
  const delta = start - item.start;
  if (delta === 0) return item;
  switch (item.type) {
    case 'asset': {
      const asset: Asset = item;
      return (
        asset.payments
          ? { ...asset, start, payments: shiftUntied(asset.payments, delta) }
          : { ...asset, start }
      ) as T;
    }
    case 'loan':
      return { ...item, start, earlyRepayments: shiftUntied(item.earlyRepayments, delta) };
    case 'flow':
    case 'formula':
      return item.end !== undefined && item.end < start && item.anchors?.end === undefined
        ? { ...item, start, end: start }
        : { ...item, start };
  }
}

/**
 * Bounds on a value `x` (now `current`) that moves some entries of a dated list along with it, so
 * that they keep their order with the entries that stay: `moves[i]` entries are at
 * `months[i] + (x − current)`. Returns [lowest, highest] x.
 */
export function orderBounds(
  months: readonly number[],
  moves: readonly boolean[],
  current: number,
): [number, number] {
  let lo = -Infinity;
  let hi = Infinity;
  let previous = -Infinity;
  months.forEach((month, i) => {
    if (!moves[i]) previous = month;
    else lo = Math.max(lo, previous - month + current + 1);
  });
  let next = Infinity;
  for (let i = months.length - 1; i >= 0; i--) {
    const month = months[i] ?? 0;
    if (!moves[i]) next = month;
    else hi = Math.min(hi, next - month + current - 1);
  }
  return [lo, hi];
}

/**
 * The months an item can start in, up to `last`, without passing the dates that stay put and must
 * come after it: a tied end or instalment, the sale, or a tied early repayment that must stay
 * within the term. Untied instalments move with the start, so they must not reach the tied ones.
 */
export function startRange(item: Item, last: number): [number, number] {
  let lo = 0;
  let hi = last;
  const bound = ([min, max]: readonly [number, number]): void => {
    lo = Math.max(lo, min);
    hi = Math.min(hi, max);
  };
  switch (item.type) {
    case 'flow':
    case 'formula':
      if (item.end !== undefined && item.anchors?.end !== undefined) bound([0, item.end]);
      break;
    case 'asset': {
      if (item.liquidateAt !== undefined) bound([0, item.liquidateAt - 1]);
      const payments = item.payments ?? [];
      for (const p of payments) if (p.anchors?.month !== undefined) bound([0, p.month]);
      bound(
        orderBounds(
          payments.map((p) => p.month),
          payments.map((p) => p.anchors?.month === undefined),
          item.start,
        ),
      );
      break;
    }
    case 'loan':
      for (const r of item.earlyRepayments) {
        if (r.anchors?.month !== undefined) bound([r.month - item.termMonths, r.month - 1]);
      }
      break;
  }
  return [lo, Math.max(lo, hi)];
}
